import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  Card,
  CardContent,
  Avatar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  LocalFireDepartment,
  LocationOn,
  CheckCircle,
  Business,
  Phone,
  Email,
  CalendarToday,
  Home,
  Warning,
  Person,
} from "@mui/icons-material";

const CountyDryerVentCleaning: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 } }}>
      {/* Blog Header */}
      <Box mb={6}>
        <Typography 
          variant="h3" 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            lineHeight: { xs: 1.3, sm: 1.4 },
            mb: { xs: 2, sm: 3 }
          }}
        >
          Annual Dryer Vent Cleaning Across the Portland Metro
        </Typography>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          align="center"
          color="text.secondary"
          sx={{
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            px: { xs: 1, sm: 0 }
          }}
        >
          What Property Managers in Every County Need to Know
        </Typography>
        
        {/* Author and Date Info */}
        <Box 
          display="flex" 
          alignItems="center" 
          justifyContent="center" 
          mt={3} 
          mb={4}
          sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
        >
          <Avatar sx={{ 
            mr: { xs: 0, sm: 2 },
            mb: { xs: 1, sm: 0 },
            width: { xs: 48, sm: 40 },
            height: { xs: 48, sm: 40 }
          }}>
            <Person />
          </Avatar>
          <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Cody Salane
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Published on January 15, 2025 • 12 min read
            </Typography>
          </Box>
        </Box>
        
        <Divider sx={{ my: 3 }} />
      </Box>

      {/* Introduction */}
      <Box mb={6} sx={{ maxWidth: '800px', mx: 'auto' }}>
        <Typography 
          variant="body1" 
          paragraph 
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.1rem' },
            lineHeight: 1.8,
            px: { xs: 1, sm: 0 }
          }}
        >
          Managing multi-family properties in the Portland Metro area comes with
          a long list of responsibilities—but one task that deserves annual
          attention is dryer vent maintenance. More than just a housekeeping
          chore, regular dryer vent cleaning is critical for fire prevention,
          code compliance, and smooth property operations.
        </Typography>

        <Typography 
          variant="body1" 
          paragraph 
          sx={{ 
            fontSize: { xs: '1rem', sm: '1.1rem' },
            lineHeight: 1.8,
            px: { xs: 1, sm: 0 }
          }}
        >
          Whether you manage units in Multnomah, Washington, Clackamas, Clark,
          or Yamhill County, here's what you need to know in 2025:
        </Typography>
      </Box>

      {/* Why It Matters Section */}
          <Box display="flex" alignItems="center" mb={2}>
            <LocalFireDepartment color="error" sx={{ mr: 1 }} />
            <Typography variant="h5">
              Why Annual Dryer Vent Cleaning Matters
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Dryer vent systems are considered part of the mechanical
            infrastructure of a building, and when neglected, they become a
            hazard:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Warning color="warning" />
              </ListItemIcon>
              <ListItemText primary="Lint accumulation is a leading cause of residential dryer fires" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="warning" />
              </ListItemIcon>
              <ListItemText primary="Blocked vents decrease airflow, increasing appliance wear and energy use" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="warning" />
              </ListItemIcon>
              <ListItemText primary="Improperly maintained vents can lead to moisture damage or carbon monoxide backdrafting" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Routine cleaning supports faster dry cycles and better tenant satisfaction" />
            </ListItem>
          </List>

      {/* County Requirements Section */}
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          mt: 4, 
          mb: 4,
          fontSize: { xs: '1.75rem', sm: '2rem' },
          px: { xs: 1, sm: 0 },
          textAlign: 'center'
        }}
      >
        County-by-County Requirements & Recommendations
      </Typography>

      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 6 }}>
        {/* Multnomah County */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationOn color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Multnomah County</Typography>
              </Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Portland, Gresham, Troutdale, etc.
              </Typography>
              <Typography variant="body2" paragraph>
                Portland enforces the Oregon Mechanical Specialty Code (OMSC)
                and Building Official Determination (BOD) 19-08, which
                addresses dryer vent terminations.
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Key regulations include:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="No screens allowed at vent terminations (screens trap lint and block airflow)" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Backdraft dampers are required to prevent outside air infiltration" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Accessible cleanouts are recommended for easy maintenance" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Louvers are permitted, but only if installed to manufacturer specifications" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Washington County */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationOn color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Washington County</Typography>
              </Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Beaverton, Hillsboro, Tigard, etc.
              </Typography>
              <Typography variant="body2" paragraph>
                Washington County also adheres to the OMSC and prioritizes
                mechanical safety inspections in multi-unit dwellings.
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Best practices:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Use rigid metal ducting—not foil or plastic" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Ensure ducts terminate outdoors and avoid long horizontal runs" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Keep total duct length within OMSC limits (35 feet max with deductions for bends)" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Clackamas County */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationOn color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Clackamas County</Typography>
              </Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Lake Oswego, Oregon City, Happy Valley, etc.
              </Typography>
              <Typography variant="body2" paragraph>
                Like other Oregon counties, Clackamas enforces the OMSC and
                recommends annual inspections of mechanical systems in
                multifamily residences.
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                Why clean annually:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Helps pass fire department inspections" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Reduces insurance risks tied to appliance fires" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Improves indoor air quality by preventing vent backflow" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Clark County */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationOn color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Clark County, WA</Typography>
              </Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Vancouver, Camas, Washougal, etc.
              </Typography>
              <Typography variant="body2" paragraph>
                Clark County follows Washington State building codes,
                including WAC 51-52-0504, which mirrors many of Oregon's
                safety expectations.
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                WA-specific tips:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Vent terminations must comply with IMC/UMC Chapter 504" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Dryer exhaust ducts must be independent and discharge outdoors" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>•</ListItemIcon>
                  <ListItemText primary="Cleanings may be required for HUD or Section 8 property compliance" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Multi-Unit Property Maintenance Tips */}
      <Box mt={4} mb={3}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Home color="primary" sx={{ mr: 1 }} />
            <Typography variant="h5">
              Multi-Unit Property Maintenance Tips
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Managing apartments, townhomes, or duplexes? Follow these
            guidelines:
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Schedule cleanings every 12 months (or more frequently if needed)" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Hire a local contractor who understands regional code distinctions" />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Request service documentation including photos and cleaning logs" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircle color="success" />
                  </ListItemIcon>
                  <ListItemText primary="Bundle multiple units for better pricing and consistent compliance" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {/* Protect Your Investment Section */}
      <Box mt={4} mb={3}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Business color="primary" sx={{ mr: 1 }} />
            <Typography variant="h5">
              Protect Your Investment and Your Tenants
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Whether you're overseeing properties in the heart of Portland,
            suburban Beaverton, or small towns like Dundee or Battle Ground, a
            proactive approach to dryer vent cleaning will:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Prevent appliance-related fires" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Keep your property aligned with Oregon and Washington codes" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Extend dryer lifespan and reduce energy bills" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircle color="success" />
              </ListItemIcon>
              <ListItemText primary="Create safer, more satisfied living environments" />
            </ListItem>
          </List>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Contact Section */}
      <Box 
        sx={{ 
          mt: { xs: 6, sm: 8 },
          py: { xs: 4, sm: 6 },
          px: { xs: 2, sm: 4 },
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Typography 
          variant="h4" 
          gutterBottom
          align="center"
          sx={{ fontSize: { xs: '1.75rem', sm: '2rem' } }}
        >
          Schedule Your Free Multi-Unit Assessment
        </Typography>
        <Typography 
          variant="body1" 
          paragraph
          align="center"
          sx={{ 
            px: { xs: 1, sm: 0 },
            mb: { xs: 3, sm: 4 },
            maxWidth: '600px',
            mx: 'auto'
          }}
        >
          Portland Dryer Mechanic is your partner in safety and compliance. We
          serve the full Portland Metro—including Multnomah, Washington,
          Clackamas, Clark, and Yamhill Counties—with affordable, professional
          dryer vent services.
        </Typography>

        <Grid 
          container 
          spacing={{ xs: 1, sm: 2 }} 
          justifyContent="center" 
          sx={{ 
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center'
          }}
        >
          <Grid item xs={12} sm="auto">
            <Button
              variant="contained"
              startIcon={<CalendarToday />}
              href="#"
              fullWidth={isMobile}
              sx={{ mb: { xs: 1, sm: 0 } }}
            >
              Annual and recurring plans
            </Button>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button
              variant="contained"
              startIcon={<Phone />}
              href="tel:5032721113"
              fullWidth={isMobile}
              sx={{ mb: { xs: 1, sm: 0 } }}
            >
              (503) 272-1113
            </Button>
          </Grid>
          <Grid item xs={12} sm="auto">
            <Button
              variant="contained"
              startIcon={<Email />}
              href="mailto:Cody@dryermechanic.com"
              fullWidth={isMobile}
            >
              Email Us
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default CountyDryerVentCleaning;